import { type Dispatch, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject, type SetStateAction } from "react";
import { type Organization } from "../api/client";
import { displayHost, displayUsername, type DecryptedItem, type ItemFilter } from "../vault/items";
import { templateFor } from "../vault/catalog";
import { type EditorState } from "./ItemEditor";
import { SearchFilters } from "./SearchFilters";
import { Dashboard } from "./Dashboard";
import { Dropdown, type MenuAction } from "./Menu";
import { type FolderNode } from "./folder-tree";
import { TypeIcon, NodeIcon, Icons } from "./icons";
import { useT } from "../i18n";

type ItemGridProps = {
  searchRef: RefObject<HTMLInputElement | null>;
  facets: ItemFilter;
  setFacets: Dispatch<SetStateAction<ItemFilter>>;
  updateFacets: (f: ItemFilter) => void;
  typeFacets: { type: string; count: number }[];
  tagFacets: string[];
  filter: "all" | "fav" | "recent";
  setFilter: (f: "all" | "fav" | "recent") => void;
  mode: "org" | "shared" | "trash";
  readOnly: boolean;
  searching: boolean;
  selectedOrg: Organization | null;
  orgs: Organization[];
  items: DecryptedItem[];
  visible: DecryptedItem[];
  selectedId: string | null;
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void;
  favorites: Set<string>;
  selectedFolderId: string | null;
  setSelectedFolderId: (id: string | null) => void;
  breadcrumb: (id: string | null) => { id: string; name: string }[];
  folderPath: (id: string | null) => string;
  selectedNode: FolderNode | null;
  setEditNode: (n: { id: string; kind: "folder" | "device"; name: string; category?: string; address?: string }) => void;
  nodeCreateActions: (parentId: string | null) => MenuAction[];
  vaultMenu: MenuAction[];
  itemActions: (it: DecryptedItem) => MenuAction[];
  setMenu: (m: { x: number; y: number; actions: MenuAction[] }) => void;
  showDashboard: boolean;
  deviceCount: number;
  folderCount: number;
  recentItems: DecryptedItem[];
  favoriteItems: DecryptedItem[];
  desktop: boolean;
  canLaunch: (it: DecryptedItem) => boolean;
  launchItem: (it: DecryptedItem) => boolean;
  launchLabel: (it: DecryptedItem) => string;
  selectItem: (id: string) => void;
  clickItem: (it: DecryptedItem, e: ReactMouseEvent) => void;
  toggleOne: (id: string) => void;
  onItemDragStart: (it: DecryptedItem, e: ReactDragEvent) => void;
  setEditor: (e: EditorState) => void;
  setImporting: (v: boolean) => void;
  setHealthOpen: (v: boolean) => void;
  setBulkEditing: (v: boolean) => void;
  setMovingIds: (ids: Set<string>) => void;
  bulkDelete: () => Promise<void>;
};

/** Middle pane: search/filter toolbar, breadcrumb, bulk bar, dashboard band, and the entries grid. */
export function ItemGrid({
  searchRef,
  facets,
  setFacets,
  updateFacets,
  typeFacets,
  tagFacets,
  filter,
  setFilter,
  mode,
  readOnly,
  searching,
  selectedOrg,
  orgs,
  items,
  visible,
  selectedId,
  selectedIds,
  setSelectedIds,
  favorites,
  selectedFolderId,
  setSelectedFolderId,
  breadcrumb,
  folderPath,
  selectedNode,
  setEditNode,
  nodeCreateActions,
  vaultMenu,
  itemActions,
  setMenu,
  showDashboard,
  deviceCount,
  folderCount,
  recentItems,
  favoriteItems,
  desktop,
  canLaunch,
  launchItem,
  launchLabel,
  selectItem,
  clickItem,
  toggleOne,
  onItemDragStart,
  setEditor,
  setImporting,
  setHealthOpen,
  setBulkEditing,
  setMovingIds,
  bulkDelete,
}: ItemGridProps) {
  const t = useT();
  return (
    <main className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border bg-panel2 px-3 py-1.5">
        <input
          ref={searchRef}
          placeholder="Search…  ( / )"
          value={facets.query}
          onChange={(e) => setFacets((f) => ({ ...f, query: e.target.value }))}
          className="w-64 rounded border border-border bg-panel px-2 py-1 text-xs outline-none focus:border-accent"
        />
        <div className="flex gap-1 text-xs">
          {(["all", "fav", "recent"] as const).map((fch) => (
            <button
              key={fch}
              onClick={() => setFilter(fch)}
              className={`rounded px-2 py-0.5 ${filter === fch ? "bg-accent text-white" : "border border-border text-muted hover:border-accent"}`}
            >
              {fch === "all" ? t("All") : fch === "fav" ? "★" : "🕘"}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted">
          {mode === "shared"
            ? `Shared with me · ${visible.length}`
            : mode === "trash"
              ? `Trash · ${selectedOrg?.name ?? ""} · ${visible.length}`
              : selectedOrg
                ? `${selectedOrg.name} · ${visible.length}`
                : "Select a vault"}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {/* Creating an item is the common case, so it is a direct button (matching the dashboard's
              one-click "New"); folders, devices and templates stay behind the caret. */}
          {mode === "org" && selectedOrg && !readOnly && (
            <span className="flex items-center">
              <button
                onClick={() => setEditor({ mode: "new" })}
                title={`${t("New item")} (n)`}
                className="rounded-l bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent2"
              >
                ＋ {t("New item")}
              </button>
              <Dropdown
                label="▾"
                title={t("New")}
                className="rounded-r border-l border-white/20 bg-accent px-1.5 py-1 text-xs font-medium text-white hover:bg-accent2"
                actions={nodeCreateActions(selectedFolderId)}
              />
            </span>
          )}
          {selectedOrg && mode !== "shared" && !readOnly && (
            <Dropdown label="⋯" title={t("Vault menu")} actions={vaultMenu} />
          )}
        </div>
      </div>
      {(items.length > 0 || searching) && (mode !== "org" || selectedOrg) && (
        <div className="flex items-center gap-2 border-b border-border bg-panel2/60 px-3 py-1.5">
          <SearchFilters filter={facets} onChange={updateFacets} types={typeFacets} tags={tagFacets} />
        </div>
      )}
      {mode === "org" && selectedOrg && selectedFolderId && !searching && (
        <div className="flex items-center gap-1.5 border-b border-border bg-panel2/60 px-3 py-1 text-xs">
          <button onClick={() => setSelectedFolderId(null)} className="text-muted hover:text-fg">{selectedOrg.name}</button>
          {breadcrumb(selectedFolderId).map((seg, i, arr) => (
            <span key={seg.id} className="flex items-center gap-1.5">
              <span className="text-muted">›</span>
              <button
                onClick={() => setSelectedFolderId(seg.id)}
                className={i === arr.length - 1 ? "font-medium" : "text-muted hover:text-fg"}
              >
                {seg.name}
              </button>
            </span>
          ))}
          {selectedNode?.kind === "device" && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-panel px-1.5 py-0.5 text-[11px] text-muted">
              <NodeIcon node={selectedNode} className="h-3 w-3" /> {selectedNode.meta?.category ?? "Other"}
              {selectedNode.meta?.address ? <span className="font-mono"> · {selectedNode.meta.address}</span> : null}
            </span>
          )}
          {selectedNode && !readOnly && (
            <button
              onClick={() =>
                setEditNode({ id: selectedNode.id, kind: selectedNode.kind, name: selectedNode.name, category: selectedNode.meta?.category, address: selectedNode.meta?.address })
              }
              className="ml-auto rounded border border-border px-2 py-0.5 hover:border-accent"
            >
              {t("Edit")}
            </button>
          )}
        </div>
      )}
      {selectedIds.size > 1 && (
        <div className="flex items-center gap-2 border-b border-accent/40 bg-accent/10 px-3 py-1.5 text-xs">
          <span className="font-medium">{selectedIds.size} {t("selected")}</span>
          <span className="text-muted">· {t("drag onto a folder/device, or")}</span>
          {mode === "org" && <button onClick={() => setBulkEditing(true)} className="rounded border border-border px-2 py-0.5 hover:border-accent">{t("Bulk edit")}</button>}
          {mode === "org" && <button onClick={() => setMovingIds(new Set(selectedIds))} className="rounded border border-border px-2 py-0.5 hover:border-accent">{t("Move to…")}</button>}
          {mode !== "shared" && <button onClick={bulkDelete} className="rounded border border-border px-2 py-0.5 text-danger hover:border-danger">{t("Delete")}</button>}
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-muted hover:text-fg">{t("Clear")}</button>
        </div>
      )}
      {showDashboard && selectedOrg && (
        <Dashboard
          orgName={selectedOrg.is_personal ? t("Personal vault") : selectedOrg.name}
          vaultCount={orgs.length}
          items={items}
          deviceCount={deviceCount}
          folderCount={folderCount}
          recent={recentItems}
          favorites={favoriteItems}
          desktop={desktop}
          onOpen={(it) => {
            if (!launchItem(it)) selectItem(it.id);
          }}
          onSelect={(it) => selectItem(it.id)}
          onNew={() => setEditor({ mode: "new" })}
          onImport={() => setImporting(true)}
          onHealth={() => setHealthOpen(true)}
        />
      )}
      <GridHeader />
      <div className="min-h-0 flex-1 overflow-auto">
        {mode === "org" && !selectedOrg && <Empty>{t("Choose a vault on the left.")}</Empty>}
        {visible.length === 0 && (mode !== "org" || selectedOrg) && (
          <Empty>
            {mode === "shared" ? t("Nothing shared with you.") : mode === "trash" ? t("Trash is empty.") : t("No items. Use “New item”.")}
          </Empty>
        )}
        {visible.map((it) => (
          <div
            key={it.id}
            draggable={mode === "org"}
            onDragStart={(e) => onItemDragStart(it, e)}
            onClick={(e) => clickItem(it, e)}
            onDoubleClick={() => {
              if (!launchItem(it) && mode !== "shared" && !readOnly) setEditor({ mode: "edit", itemId: it.id });
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              if (!selectedIds.has(it.id)) selectItem(it.id);
              setMenu({ x: e.clientX, y: e.clientY, actions: itemActions(it) });
            }}
            className={`group grid w-full cursor-default grid-cols-[2fr_1fr_1.5fr_1.5fr_4rem] items-center px-3 py-1.5 text-left hover:bg-panel2 ${
              selectedIds.has(it.id) ? "bg-accent/20" : ""
            } ${selectedId === it.id ? "ring-1 ring-inset ring-accent/40" : ""}`}
          >
            <span className="flex items-center gap-1.5 truncate">
              <input
                type="checkbox"
                checked={selectedIds.has(it.id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleOne(it.id)}
                className={`h-3 w-3 shrink-0 ${selectedIds.has(it.id) ? "" : "opacity-0 group-hover:opacity-100"}`}
              />
              <span className="shrink-0 text-muted">
                {favorites.has(it.id) ? <Icons.star className="h-4 w-4 fill-warn text-warn" /> : <TypeIcon type={it.type} />}
              </span>
              <span className="min-w-0 truncate">
                <span className="truncate">{it.content.name}</span>
                {searching && it.folderId && <span className="block truncate text-[10px] text-muted">{folderPath(it.folderId) || "/"}</span>}
              </span>
            </span>
            <span className="truncate text-muted">{templateFor(it.type).label}</span>
            <span className="truncate font-mono text-muted">{displayHost(it.content) || "—"}</span>
            <span className="truncate font-mono text-muted">{displayUsername(it.content) || "—"}</span>
            <span className="flex items-center justify-end gap-1">
              <span className="hidden items-center gap-1 group-hover:flex">
                {canLaunch(it) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      launchItem(it);
                    }}
                    title={launchLabel(it)}
                    className="grid h-6 w-6 place-items-center rounded text-accent hover:bg-accent/20"
                  >
                    <Icons.play className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    selectItem(it.id);
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setMenu({ x: r.left, y: r.bottom + 2, actions: itemActions(it) });
                  }}
                  title={t("Actions")}
                  className="grid h-6 w-6 place-items-center rounded text-muted hover:bg-accent/20 hover:text-fg"
                >
                  <Icons.more className="h-3.5 w-3.5" />
                </button>
              </span>
              <span className="font-mono text-muted group-hover:hidden">r{it.revision}</span>
            </span>
          </div>
        ))}
      </div>
    </main>
  );
}

function GridHeader() {
  const t = useT();
  return (
    <div className="grid grid-cols-[2fr_1fr_1.5fr_1.5fr_4rem] border-b border-border bg-panel px-3 py-1.5 text-[11px] font-semibold text-muted">
      <div>{t("Name")}</div>
      <div>{t("Type")}</div>
      <div>{t("Host / URL")}</div>
      <div>{t("Username")}</div>
      <div className="text-right">{t("Rev")}</div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="grid h-full place-items-center px-6 text-center text-sm text-muted">{children}</div>;
}
